import { FilesApi, FileUploadDTO } from "api/FilesApi";
import {
  useDeleteCompanyFile,
  useDeletePublicFile,
  useGetCompanyFiles,
  useGetPublicFiles,
} from "api/api-hooks/file-hooks";
import UserChip from "components/basic/user-chip/UserChip";
import { useHasPermissions } from "hooks/useHasPermissions";
import { useDisplayTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import React, { useState } from "react";
import { fmtDatetime } from "utils/time-utils";
import EasasPageTemplateWrapper from "../../templates/EasasPageTemplateWrapper";
import { PermissionGuard } from "../../wrappers/PermissionGuard";
import CompanyFileUploadDialog from "./dialogs/CompanyFileUploadDialog";
import PublicFileUploadDialog from "./dialogs/PublicFileUploadDialog";

export default function CompanyFilesPage() {
  const displayTz = useDisplayTimezone();

  const isAllowedCompanyView = useHasPermissions("COMPANY_FILES_VIEW");

  const publicFiles = useGetPublicFiles();
  const companyFiles = useGetCompanyFiles(isAllowedCompanyView);

  const [publicFileDialogVisible, setPublicFileDialogVisible] = useState(false);
  const [companyFileDialogVisible, setCompanyFileDialogVisible] = useState(false);

  const [currentFile, setCurrentFile] = useState<FileUploadDTO | undefined>();

  const tableRowButtonTemplate = (rowData: FileUploadDTO) => {
    return (
      <div>
        <PermissionGuard permissions={rowData.isPublic ? "PUBLIC_FILES_EDIT" : "COMPANY_FILES_EDIT"}>
          <EditButton
            file={rowData}
            setVisible={setCompanyFileDialogVisible}
            setVisiblePublic={setPublicFileDialogVisible}
            setCurrentFile={setCurrentFile}
          />
          <DeleteButton file={rowData} />
        </PermissionGuard>
      </div>
    );
  };

  const fileNameTemplate = (rowData: FileUploadDTO) => {
    return <DownloadHref file={rowData} />;
  };

  const tagsBodyTemplate = (rowData: FileUploadDTO) => {
    // @ts-ignore
    return rowData.tags?.map((tag, index) => <Tag key={tag.concat(index)} value={tag} className="p-mr-2" />);
  };

  const createdAtBodyTemplate = (rowData: FileUploadDTO) => {
    return <span>{fmtDatetime(rowData.createdAt, displayTz)}</span>;
  };

  const editedAtBodyTemplate = (rowData: FileUploadDTO) => {
    return <span>{fmtDatetime(rowData.editedAt, displayTz)}</span>;
  };

  const createdByTemplate = (rowData: FileUploadDTO) => {
    return (
      <span>
        <UserChip id={rowData.createdBy} />
      </span>
    );
  };

  return (
    <EasasPageTemplateWrapper breadcrumb={["Company files"]} title="Files" cardStyle={{ height: "100%" }}>
      <TabView activeIndex={0}>
        {isAllowedCompanyView && (
          <TabPanel header="Company files">
            <PermissionGuard permissions="COMPANY_FILES_EDIT">
              <Button
                label="Upload file..."
                icon="pi pi-upload"
                className="p-mb-4"
                onClick={() => {
                  setCompanyFileDialogVisible(true);
                }}
              />
            </PermissionGuard>

            <div>
              {companyFiles.isLoading && "Loading...."}
              {companyFiles.isError && "Error loading files"}
            </div>

            <DataTable value={companyFiles.data ?? []}>
              <Column header="File Name" body={fileNameTemplate} />
              <Column field="description" header="Description" />
              <Column field="tags" header="Tags" body={tagsBodyTemplate} />
              <Column header="Created" body={createdAtBodyTemplate} />
              <Column header="Created By" body={createdByTemplate} />

              <Column body={tableRowButtonTemplate} style={{ width: "100px" }} />
            </DataTable>
            <CompanyFileUploadDialog
              visible={companyFileDialogVisible}
              setVisible={setCompanyFileDialogVisible}
              currentFile={currentFile}
              setCurrentFile={setCurrentFile}
            />
          </TabPanel>
        )}
        <TabPanel header="Public files">
          <PermissionGuard permissions="PUBLIC_FILES_EDIT">
            <Button
              label="Upload file..."
              icon="pi pi-upload"
              className="p-mb-4"
              onClick={() => {
                setPublicFileDialogVisible(true);
              }}
            />
          </PermissionGuard>

          <div>
            {publicFiles.isLoading && "Loading...."}
            {publicFiles.isError && "Error loading files"}
          </div>

          <DataTable value={publicFiles.data ?? []}>
            <Column header="File Name" body={fileNameTemplate} />
            <Column field="description" header="Description" />
            <Column header="Tags" body={tagsBodyTemplate} />
            <Column header="Edited" body={editedAtBodyTemplate} />

            <Column body={tableRowButtonTemplate} style={{ width: "100px" }} />
          </DataTable>
          <PublicFileUploadDialog
            visible={publicFileDialogVisible}
            setVisible={setPublicFileDialogVisible}
            currentFile={currentFile}
            setCurrentFile={setCurrentFile}
          />
        </TabPanel>
      </TabView>
    </EasasPageTemplateWrapper>
  );
}

function DownloadHref(props: { file: FileUploadDTO }) {
  const fileUrl = props.file.isPublic
    ? FilesApi.downloadPublicFileUrl(props.file.downloadUrlPart)
    : FilesApi.downloadCompanyFileUrl(props.file.downloadUrlPart);

  return (
    <a href={fileUrl}>
      {props.file.fileName} <i className="ml-2 pi pi-download" />
    </a>
  );
}

function EditButton(props: {
  file: FileUploadDTO;
  setVisible: (visible: boolean) => void;
  setVisiblePublic: (visible: boolean) => void;
  setCurrentFile(file: FileUploadDTO): void;
}) {
  const openDialog = () => {
    if (props.file.isPublic) {
      props.setVisiblePublic(true);
    } else {
      props.setVisible(true);
    }
    props.setCurrentFile(props.file);
  };

  return (
    <Button icon="pi pi-pencil" className=" p-button p-button-rounded p-button-text" onClick={openDialog} />
  );
}

function DeleteButton(props: { file: FileUploadDTO }) {
  const deletePubFile = useDeletePublicFile();
  const deleteCompFile = useDeleteCompanyFile();

  const deleteMutation = props.file.isPublic ? deletePubFile : deleteCompFile;

  const deleteFile = () => {
    deleteMutation.mutate(props.file.downloadUrlPart);
  };

  return (
    <Button
      icon="pi pi-trash"
      className=" p-button p-button-rounded p-button-text p-button-danger"
      onClick={deleteFile}
    />
  );
}
